import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "components/ui/form";
import { InputGs, SelectGs } from "components/ui/Forms";
import DatePickerGs from "components/ui/Forms/datePickerGs";
import { Button } from "components/ui/ButtonGs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useErrorHandler } from "exceptions/ErrorHandler";
import { formSchema } from "../../formSchema";
import { useHubDriver, useCities, useHubCarrier, useDriver } from "hooks/api";
import { useHistory } from "react-router-dom";
import { customMask } from "lib/customMask";
import { toast } from "sonner";
import { IHubDriver } from "interfaces/Hub";
import { format } from "date-fns";
import { listStatesOptions } from "utils/data/states";

type UpdateFormProps = {
  data?: IHubDriver;
};

export const UpdateForm: React.FC<UpdateFormProps> = ({ data }) => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { errorHandler } = useErrorHandler();
  const { updateDriver } = useHubDriver();
  const { getCities, getCEP } = useCities();
  const { fetchCarriers } = useHubCarrier();
  const { findDriver } = useDriver();
  // Estado selecionado
  const [selectedState, setSelectedState] = React.useState<string>("");
  const [cep, setCep] = React.useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const mutation = useMutation(updateDriver, {
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries("updateDriver");
      toast.success("Motorista atualizado com sucesso!");
      navigate.push("/settings/hub-drivers");
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const mutationFindDriver = useMutation(findDriver, {
    onSuccess: (data) => {
      form.setValue("name", data?.name || "");
      if (data?.birth_date) {
        const dt = new Date(data.birth_date);
        form.setValue("birth_date", dt || undefined);
      }
    },
    onError: () => {
      form.resetField("name");
      form.resetField("birth_date");
    },
  });

  const onSubmit = (formData: z.infer<typeof formSchema>) => {
    const cleanDocument = formData.document?.replace(/[^\d]/g, "") || "";
    const birth_date = format(new Date(formData.birth_date), "yyyy-MM-dd");
    mutation.mutate({
      ...formData,
      birth_date,
      document: cleanDocument,
      id: data?.id as number,
    });
  };

  const onSuccessCEP = useCallback(
    (data: any) => {
      form.setValue("address_street", data.address_street || "");
      form.setValue("address_neighborhood", data.address_neighborhood || "");
      form.setValue("address_city", data.address_city || "");
      form.setValue("address_state", data.address_state || "");
      form.setValue("address_id_city_ibge", data.address_id_city_ibge || "");
    },
    [form]
  );

  const {
    data: dataCities,
    isLoading: isLoadingCities,
    refetch: refetchCities,
  } = useQuery({
    queryKey: ["getCities", selectedState],
    queryFn: () => getCities({ state: selectedState }),
    enabled: false,
  });

  const { refetch: refetchCEP } = useQuery({
    queryKey: ["getCEP", cep],
    queryFn: () => getCEP(cep),
    enabled: false,
    onSuccess: (response: any) => {
      setCep(() => null);
      onSuccessCEP(response);
    },
  });

  const { data: carriers, isLoading: loadinDocTypes } = useQuery({
    queryKey: ["fetchCarriers"],
    queryFn: () => fetchCarriers(),
    staleTime: 1000 * 60 * 5, // 5 min,
    refetchOnWindowFocus: false,
  });

  const handleCityChange = useCallback((state: string) => {
    setSelectedState(() => state);
  }, []);

  const handleDocumentChange = useCallback((document: string) => {
    const cleanDocument = document?.replace(/[^\d]/g, "") || "";
    if (cleanDocument.length === 11) {
      mutationFindDriver.mutate(cleanDocument);
    }
  }, [mutationFindDriver]);

  const handleCepChange = useCallback((cep: string) => {
    const cleanCEP = cep ? cep.replace(/[^0-9]/g, "") : null;

    if (cleanCEP && cleanCEP.length === 8) {
      setCep(cleanCEP);
    } else {
      setCep(null);
      toast.warning("CEP inválido!");
    }
  }, []);

  useEffect(() => {
    if (selectedState) {
      refetchCities();
    }
  }, [selectedState, refetchCities]);

  useEffect(() => {
    if (cep) {
      refetchCEP();
    }
  }, [cep, refetchCEP]);

  useEffect(() => {
    if (data) {
      if (!dataCities && data.address_state) {
        handleCityChange(data.address_state);
      }
      form.reset({
        ...data,
        birth_date: new Date(data.birth_date),
        carrier_id: String(data.carrier_id),
        document: customMask(data.document, "999.999.999-99"),
      });
    }
  }, [data, dataCities, handleCityChange, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <FormField
            name="carrier_id"
            control={form.control}
            render={({ field }) => (
              <SelectGs
                {...field}
                label={"Transportadora"}
                onValueChange={(value: string) => {
                  field.onChange(value);
                }}
                content={carriers?.comboOptions || []}
                isLoading={loadinDocTypes}
                value={field.value ?? undefined}
              />
            )}
          />

          <FormField
            name="document"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                label="CPF"
                value={field.value ?? ""}
                onChange={(e) => {
                  field.onChange(customMask(e.target.value, "999.999.999-99"));
                  handleDocumentChange(e.target.value);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="birth_date"
            render={({ field }) => (
              <DatePickerGs
                {...field}
                label={"Data de Nascimento"}
                fieldValue={field.value}
                fieldOnChange={field.onChange}
                showMonthYearPicker
                isLoading={mutationFindDriver.isLoading}
              />
            )}
          />

          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                label="Nome"
                value={field.value ?? ""}
                isLoading={mutationFindDriver.isLoading}
              />
            )}
          />

          <FormField
            name="phone_number"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                label="Telefone"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(customMask(e.target.value, "(99) 99999-9999"))
                }
              />
            )}
          />
          {/* <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} value={field.value ?? ""} label="Email" />
            )}
          /> */}
          <FormField
            name="address_zipcode"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                value={field.value ?? ""}
                label="CEP"
                onBlur={(e: any) => handleCepChange(e.target.value)}
                onChange={(e) =>
                  field.onChange(customMask(e.target.value, "99999-999"))
                }
              />
            )}
          />

          <FormField
            name="address_state"
            control={form.control}
            render={({ field }) => (
              <SelectGs
                {...field}
                label={"UF"}
                onValueChange={(value: string) => {
                  field.onChange(value);
                  handleCityChange(value);
                }}
                content={listStatesOptions || []}
                isLoading={false}
                value={field.value ?? undefined}
              />
            )}
          />
          <FormField
            name="address_city"
            control={form.control}
            render={({ field }) => (
              <SelectGs
                {...field}
                label={"Cidade"}
                onValueChange={field.onChange}
                content={dataCities || []}
                isLoading={isLoadingCities}
                value={field.value ?? undefined}
              />
            )}
          />

          <FormField
            name="address_id_city_ibge"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                value={field.value ?? ""}
                label="IBGE"
                hidden
              />
            )}
          />
          <FormField
            name="address_street"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                value={field.value ?? ""}
                label="Logradouro"
              />
            )}
          />
          <FormField
            name="address_number"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} value={field.value ?? ""} label="Número" />
            )}
          />
          <FormField
            name="address_neighborhood"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} value={field.value ?? ""} label="Bairro" />
            )}
          />
        </div>
        <div className="flex justify-end w-full gap-4">
          <Button.Root
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => navigate.push("/settings/hub-drivers")}
          >
            Cancelar
          </Button.Root>
          <Button.Root
            variant="primary"
            size="sm"
            type="submit"
            disabled={mutation.isLoading || !data}
          >
            Salvar
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};
